import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { message, Tooltip, Button, Modal, Card } from "antd";
import {
  chatboticon,
  usermascoticon,
  botmascoticon,
} from "../../assets/images/images";
import { FetchMessagesFromLibrary } from "@utils/utils.js";
import {
  CloseOutlined,
  SendOutlined,
  StopOutlined,
  ReloadOutlined,
  AudioOutlined,
  MoreOutlined,
  DownloadOutlined,
  EyeFilled,
  LikeOutlined,
  DislikeOutlined,
  CopyTwoTone,
  PaperClipOutlined,
} from "@ant-design/icons";
import ReadMoreText from "./ReadMoreText";
import "./FooterComponent.scss";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import AlertComponent from "../../components/alerts/AlertComponent.js";
import { v4 as uuidv4 } from "uuid";
import AttachmentOptions from "./AttachmentOptions.js";
import { getData, environment } from "@services/apiConfig";
import { applicationconf } from "../../constants/endpoints.js";

const initialMsg = {
  sender: "Bot",
  message: "Loading...", // Use fetched message here
  chartPhotos: [],
  messageId: null,
};

const FooterComponent = () => {
  const userDetailReducer = useSelector(
    (state) => state.basicConfigReducer?.userdetails || null
  );

  const [userDetails, setuserDetails] = useState(userDetailReducer);

  const [isChatOpen, setIsChatOpen] = useState(false); // State to control the visibility of the chat window
  const [userInput, setUserInput] = useState(""); // State to manage the input field value
  const [showIcons, setShowIcons] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [isLiked, setIsLiked] = useState(false);
  const [isDisLiked, setIsDisLiked] = useState(false);
  const [showAttachmentOptions, setShowAttachmentOptions] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([initialMsg]);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(""); // Track the process status message
  const [isListening, setIsListening] = useState(false); // State for listening status
  const [recognition, setRecognition] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [botIcon, setBotIcon] = useState(botmascoticon); // State for bot mascot
  const [isChatBotConfigScreenUsed, setIsChatBotConfigScreenUsed] =
    useState(false);

  const chatEndRef = useRef(null);
  const abortControllerRef = useRef(null); // Keep a ref for AbortController
  const suggestionAbortControllerRef = useRef(null); // For suggestions
  const narrativeFetchedRef = useRef(false); // Declare at the component level


  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    getChatScreenViewStatus();
  }, []);

  useEffect(() => {
    const greetMsg = async () => {
      try {
        let message = await FetchMessagesFromLibrary({ msgcode: "E232" });
        console.log("API Message:", message);
        setConversationHistory([{ ...initialMsg, message }]);
        if (isChatBotConfigScreenUsed) {
          checkChatbotConfig();
        }
      } catch (error) {
        console.error("Error fetching message:", error);
      }
    };
    greetMsg();
  }, []);

  const checkChatbotConfig = async () => {
    try {
      // Get Config Data for chat window
      let mapDataParams = {
        url: `getchatbotconfig`,
        baseName: "core",
      };
      let response = await getData(mapDataParams);
      console.log("Config Data:", response.mascot);

      // Set chatbot greeting message
      let message = response.mascot?.greeting || initialMsg.message; // ✅ Proper fallback
      setConversationHistory([{ ...initialMsg, message }]);

      // Process the chatbot icon
      if (response.mascot?.avatar?.data) {
        let avatarData = response.mascot.avatar.data;

        // Convert Buffer to Base64 if necessary
        if (typeof avatarData !== "string") {
          if (avatarData.type === "Buffer" && Array.isArray(avatarData.data)) {
            avatarData = btoa(
              String.fromCharCode(...new Uint8Array(avatarData.data))
            );
          } else {
            console.error("Unexpected avatar data format:", avatarData);
            return; // Exit if the format is incorrect
          }
        }

        // Convert to Data URL format
        const dataUrl = avatarData.startsWith("data:")
          ? avatarData
          : `data:${response.mascot.avatar.contentType};base64,${avatarData}`;

        setBotIcon(dataUrl); // ✅ Update bot icon dynamically
      }
    } catch (error) {
      console.error("Error fetching chatbot configuration:", error);
    }
  };

  useEffect(() => {
    if (userDetailReducer) {
      setuserDetails(userDetailReducer);
    }
  }, [userDetailReducer]);

  useEffect(() => {
    if (userDetails) {
      // Generate session ID based on userDetails._id and current timestamp
      const generatedSessionId = `${userDetails._id}-${new Date().getTime()}`;
      setSessionId(generatedSessionId);
    }
  }, [userDetails]);

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory, loading]);

  const handleImageClick = (image) => {
    console.log("image", image);
    setPreviewImage(image);
    setShowModal(true);
  };

  // New function to create a PDF with narrative and image
  const handleDownloadNarrativePDF = async (messageData, index) => {
    const doc = new jsPDF("p", "mm", "a4");
    let yPosition = 20; // Initial Y position for text

    // Add sender ID and narrative text on the first page
    doc.setFontSize(14);
    doc.text(`Message from: ${messageData.sender}`, 10, yPosition); // Sender ID
    yPosition += 10;

    if (messageData.message) {
      doc.setFontSize(12);
      const textLines = doc.splitTextToSize(
        `Narrative: ${messageData.message}`,
        180
      ); // Wrap text to fit within 180mm
      doc.text(textLines, 10, yPosition); // Add the wrapped text
      yPosition += textLines.length * 8; // Adjust Y position based on text length
    }

    // Check if the message has associated images and add them on separate pages
    if (messageData.chartPhotos) {
      if (
        Array.isArray(messageData.chartPhotos) &&
        messageData.chartPhotos.length > 0
      ) {
        const imagePromises = messageData.chartPhotos.map((photo, idx) => {
          const canvasElement = document.querySelector(
            `#chart-${index}-${idx}`
          );
          // Use html2canvas with increased scale for high-resolution images
          return html2canvas(canvasElement, { scale: 3 });
        });

        // Wait until all images are rendered
        const canvasArray = await Promise.all(imagePromises);

        canvasArray.forEach((canvas, idx) => {
          const imgData = canvas.toDataURL("image/png");
          const imgWidth = 180; // Image width for the PDF
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Add a new page for each image
          if (idx > 0 || yPosition + imgHeight > 290) {
            doc.addPage();
            yPosition = 20; // Reset Y position
          }

          // Add the high-quality image to the PDF
          doc.addImage(imgData, "PNG", 10, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10; // Adjust Y position for the next image
        });

        // Save the PDF with a unique name for the response
        doc.save(`message-narrative-${index + 1}.pdf`);
      } else {
        // Handle the single string case
        const canvasElement = document.querySelector("#chart");
        if (canvasElement) {
          const canvas = await html2canvas(canvasElement, { scale: 3 });
          const imgData = canvas.toDataURL("image/png");
          const imgWidth = 180; // Image width for the PDF
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Add the single image to the PDF
          doc.addImage(imgData, "PNG", 10, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 10;

          // Save the PDF with a unique name for the response
          doc.save(`message-narrative-${index + 1}.pdf`);
        } else {
          console.warn("Canvas element not found for chartPhoto.");
        }
      }
    } else {
      // Save the PDF directly if there are no images
      doc.save(`message-narrative-${index + 1}.pdf`);
    }
  };

  // Function to add messages to the history
  const addMessageToHistory = (
    sender,
    message,
    chartPhotos = [],
    messageId = 100
  ) => {
    setConversationHistory((prevHistory) => [
      ...prevHistory,
      {
        sender,
        message,
        chartPhotos,
        ...(messageId && { messageId }), // Add messageId only if it exists
      },
    ]);
    scrollToBottom(); // Ensure scroll works
  };

  const toggleIconSlider = () => {
    setShowIcons((prev) => !prev); // Toggle the visibility of the icon slider
  };

  async function handleChatSubmit(inputValue = null) {
    const trimmedUserInput = inputValue ? inputValue : userInput.trim();
    narrativeFetchedRef.current = false;
    if (!trimmedUserInput) return;
    // Abort any ongoing suggestion API call before chat submission
    if (suggestionAbortControllerRef.current) {
      suggestionAbortControllerRef.current.abort();
    }
    setSuggestions([]); // Clear suggestions
    suggestionAbortControllerRef.current = null;

    // Add user message to history
    addMessageToHistory("User", trimmedUserInput);

    // Show loading spinner
    setLoading(true);
    setUserInput(""); // Clear the input box
    setStatusMessage("Searching..."); // Initial status message

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Start the long-running process on the server
      await fetch(
        `${process.env.REACT_APP_PUBLIC_URL}/webBotService/botService-generate-narrative`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({
            source: "Nigeria Situation room: Web",
            session_id: sessionId, // Replace with actual session ID if needed
            user_id: userDetails._id,
            query: trimmedUserInput, // User input as the narrative prompt
          }),
          signal: controller.signal,
        }
      );

      // Poll the status every second
      const interval = setInterval(async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_PUBLIC_URL}/webBotService/current_status?session_id=${sessionId}`,
            {
              // Corrected session_id in the URL
              method: "GET", // GET request should not have a body
              headers: {
                "Content-Type": "application/json", // Content-Type is optional for GET
              },
              signal: controller.signal, // For handling request cancellation
            }
          );
          const data = await response.json();
          // Dynamically update the message text based on the status
          setStatusMessage(`Process status: ${data.status}`);

          // Check for the correct completion status
          // if (data.status === "Processing finished, Displaying results") {
          //   clearInterval(interval); // Stop checking when the process is complete
          //   await handleApiCall(trimmedUserInput); // Fetch the final narrative and other data
          //   setStatusMessage(null); // Remove fetching message
          //   setLoading(false); // Hide loading spinner
          // }

          if (
            data.status === "Processing finished, Displaying results" &&
            !narrativeFetchedRef.current
          ) {
            narrativeFetchedRef.current = true; // Ensure it's only triggered once
            clearInterval(interval);
            await handleApiCall(trimmedUserInput);
            setStatusMessage(null);
            setLoading(false);
          }

        } catch (error) {
          if (error.name !== "AbortError") {
            // Handle normal errors
            console.error("Error fetching status:", error);
          }
        }
      }, 1000); // Poll the status every second
    } catch (error) {
      if (error.name !== "AbortError") {
        // Handle normal errors
        console.error("Error:", error);
        addMessageToHistory(
          "Bot",
          "An error occurred while starting the process."
        );
      }
      setStatusMessage(null);
      setLoading(false); // Hide the loading spinner
    }
  }

  const handleApiCall = async (userInput) => {
    setLoading(true); // Show loading spinner

    try {
      // Fetch the narrative from the backend
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_URL}/webBotService/get_narrative?session_id=${sessionId}`,
        {
          method: "GET", // No body for GET requests
          headers: {
            "Content-Type": "application/json", // Content-Type is optional for GET
          },
        }
      );

      // Check if the narrative response is valid
      if (response.ok) {
        const { narrative, charts, messageId } = await response.json();
        addMessageToHistory(
          "Bot",
          narrative,
          charts,
          messageId ? messageId : "10"
        ); // this is 10 is given as dummy message id
      } else {
        addMessageToHistory("Bot", "Failed to retrieve the narrative.");
      }
    } catch (error) {
      addMessageToHistory("Bot", "An error occurred while fetching data.");
    } finally {
      setLoading(false); // Hide loading spinner
      setUserInput(""); // Clear the input box
    }
  };

  const handleChatbotClick = () => {
    setIsChatOpen(!isChatOpen); // Toggle chat window visibility
  };

  const handleCloseChatWindow = () => {
    setIsChatOpen(false); // Close the chat window
  };

  const startVoiceRecognition = () => {
    let voiceInput = ""; // Declare voiceInput in a higher scope

    if (!recognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    recognition.start();
    setIsListening(true);

    recognition.onresult = (event) => {
      voiceInput = event.results[0][0].transcript;
      setUserInput(voiceInput);
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      alert("Error capturing voice. Please try again.");
    };

    recognition.onend = (event) => {
      setIsListening(false);
      if (voiceInput) {
        toggleIconSlider();
        handleChatSubmit(voiceInput);
      }
    };
  };

  // Set the default greeting message when the chatbot is opened
  useEffect(() => {
    try {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.lang = "en-US";
        recognitionInstance.interimResults = false;
        setRecognition(recognitionInstance);
      } else {
        alert("Speech recognition is not supported in this browser.");
      }
    } catch (error) {
      console.error("Error initializing Speech Recognition:", error);
    }
  }, []); // Empty dependency array ensures it runs once when the component mounts

  const handleReset = () => {
    handleCancelRequest();
    setConversationHistory([initialMsg]);
  };

  const handleCancelRequest = () => {
    setIsListening(false);
    // Stop the recognition process if it's running
    if (recognition) {
      recognition.stop();
      console.log("Speech recognition stopped.");
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Cancel the ongoing request
      setLoading(false);
    } else {
      console.log("No ongoing request to cancel.");
    }
  };

  const handleSuggestions = async (event) => {
    const userInput = event.target.value.trim();

    // Clear suggestions if input is empty or fewer than 5 characters
    if (!userInput || userInput.length < 5) {
      setSuggestions([]); // Clear suggestions
      return;
    }

    if (suggestionAbortControllerRef.current) {
      console.log("suggestionAbortControllerRef", suggestionAbortControllerRef);
      suggestionAbortControllerRef.current.abort();
    }

    const suggestionController = new AbortController();
    suggestionAbortControllerRef.current = suggestionController;
    console.log("suggestionController set", suggestionController);

    // Prevent API calls if a request is already in progress
    // if (isRequestInProgress) {
    //   console.log(
    //     "Request already in progress. Waiting for the current response."
    //   );
    //   return;
    // }

    // Set the flag indicating a request is in progress
    setIsRequestInProgress(true);
    let url = `${process.env.REACT_APP_PUBLIC_URL}/webBotService/autocomplete`;
    const formData = new FormData();
    formData.append("query", userInput); // Add the user input query

    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData, // Sending form data (query and compressed contextFields)
        signal: suggestionController.signal, // Use suggestions-specific AbortController
      });
      const data = await response.json(); // Parse the response as JSON

      // Ensure response.suggestions exists and is an array
      if (
        !data ||
        !Array.isArray(data.suggestions) ||
        typeof data.title !== "string"
      ) {
        console.error("Unexpected response format:", response);
        setSuggestions([]); // Clear suggestions
        return;
      }

      const cleanedSuggestions = data.suggestions.map(
        (suggestion) => suggestion.replace(/\\?"/g, "") // Clean up unwanted quotes if needed
      );

      // Set suggestions in the state
      setSuggestions(cleanedSuggestions);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Suggestion request was aborted.");
      } else {
        console.error("Error fetching suggestions:", error);
      }
      setSuggestions([]); // Clear suggestions on error
    } finally {
      // Reset the flag to allow new requests
      setIsRequestInProgress(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setUserInput(suggestion); // Set input box value
    handleChatSubmit(suggestion);
  };

  const onThumbsUp = (messageObj) => {
    setIsLiked(!isLiked);
    setConversationHistory((prev) =>
      prev.map((msg) =>
        msg.messageId === messageObj?.messageId
          ? { ...msg, thumbsUp: true }
          : msg
      )
    );
    if (messageObj.message) {
      sendFeedbackToBackend(
        messageObj.messageId,
        1,
        userDetails._id,
        sessionId
      );
    }
  };

  const onThumbsDown = (messageObj) => {
    setIsDisLiked(!isDisLiked);

    setConversationHistory((prev) =>
      prev.map((msg) =>
        msg.id === messageObj?.messageId ? { ...msg, thumbsDown: true } : msg
      )
    );

    if (messageObj.message) {
      // Send feedback after thumbs-up with additional data
      sendFeedbackToBackend(
        messageObj.messageId,
        0,
        userDetails._id,
        sessionId
      );
    }
  };

  const sendFeedbackToBackend = async (
    messageid,
    feedbackType,
    userId,
    sessionId
  ) => {
    const feedbackData = {
      messageid: messageid, //unique msg id
      feedback: feedbackType, // Thumbs-up or other feedback type
      userId: userId, // User ID
      sessionId: sessionId, // Session ID
    };
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_URL}/webBotService/save_feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(feedbackData),
        }
      );

      if (response.ok) {
        setLoading(false);

        console.log("Feedback saved successfully");
        AlertComponent({
          type: "success",
          title: "Success",
          text: "Your feedback has been submitted. Thank you!",
          icon: "success",
        });
      } else {
        AlertComponent({
          type: "error",
          title: "Error",
          text: "Failed to save feedback",
          icon: "error",
        });
      }
    } catch (error) {
      AlertComponent({
        type: "error",
        title: "Error",
        text: `Error while sending feedback to the backend:", ${error}`,
        icon: "error",
      });
      console.error("Error while sending feedback to the backend:", error);
    }
  };

  const handleCopyMessage = (message) => {
    navigator.clipboard.writeText(message).then(() => {
      AlertComponent({
        type: "success",
        title: "Success",
        text: "Message copied to clipboard",
        icon: "success",
      });
    });
  };

  const toggleAttachmentOptions = () => {
    setShowAttachmentOptions((prev) => !prev);
  };

  const clearSuggestions = () => {
    if (suggestionAbortControllerRef.current) {
      console.log("suggestionAbortControllerRef", suggestionAbortControllerRef);
      suggestionAbortControllerRef.current.abort();
    }
    setSuggestions([]); // Clear suggestions
    suggestionAbortControllerRef.current = null;
  };

  const getChatScreenViewStatus = async () => {
    let url = `${applicationconf}?confkey=showChatbotPanelChatScreen`;
    let response = await getData({ url });
    if (response?.confvalue === true) {
      setIsChatBotConfigScreenUsed(true);
    } else {
      setIsChatBotConfigScreenUsed(false);
    }
  };

  return (
    <>
      {/* Chatbot Icon */}
      <div className="footer">
        <div
          className="chatbot-icon"
          onClick={() => {
            handleChatbotClick();
            if (isChatBotConfigScreenUsed) {
              checkChatbotConfig();
            }
          }}
        >
          <Tooltip title="Chat with us">
            <img src={chatboticon} alt="Chatbot Icon" className="pulsing" />
          </Tooltip>
        </div>
      </div>

      {/* NEW CODE FOR NIGERIA */}
      {isChatBotConfigScreenUsed && isChatOpen && (
        <div className="narrative-container" style={{ padding: "0px" }}>
          <button
            onClick={handleCloseChatWindow}
            className="close-chat-button"
            title="Close Chat"
          >
            <CloseOutlined /> {/* Ant Design Close Icon */}
          </button>
          <iframe
            src={`${environment.baseUrlUAT}/botService?id=${userDetails._id}`}
            //src={`http://localhost:3001/botService?id=${userDetails._id}`}
            style={{
              //width: "100%",
              height: "200%",
              minHeight: "81vh", // Ensures visibility
              border: "none",
            }}
          />
        </div>
      )}

      {/* Chat Window */}
      {/* OLD CODE FOR ZIMBABWE */}
      {!isChatBotConfigScreenUsed && isChatOpen && (
        <div className="narrative-container">
          <button
            onClick={handleCloseChatWindow}
            className="close-chat-button"
            title="Close Chat"
          >
            <CloseOutlined /> {/* Ant Design Close Icon */}
          </button>
          {/* Conversation History */}
          <div className="conversation-history" id="conversation-history">
            {conversationHistory.map((messageObj, index) => (
              <div
                key={index}
                className={`message-container ${messageObj.sender === "User" ? "user-message" : "bot-message"}`}
                style={{
                  alignItems:
                    messageObj.sender === "User" ? "flex-end" : "flex-start",
                }}
              >
                <span
                  className={`message-text ${messageObj.sender === "User" ? "user-color" : "bot-color"}`}
                >
                  {/* <span className="sender-label">
                      {messageObj.sender === "User" ? "User: " : "BrainIQ: "}
                    </span> */}
                  <img
                    src={
                      messageObj.sender === "User" ? usermascoticon : botIcon
                    }
                    alt={
                      messageObj.sender === "User"
                        ? "User Mascot"
                        : "Bot Mascot"
                    }
                    className="mascot-image"
                    style={{
                      width: "40px",
                      height: "40px",
                      marginRight: "10px",
                    }}
                  />

                  {messageObj.sender !== "User" ? (
                    <ReadMoreText
                      messageObj={messageObj}
                      text={messageObj.message}
                      onThumbsUp={onThumbsUp}
                      onThumbsDown={onThumbsDown}
                      handleCopyMessage={handleCopyMessage}
                      isLiked={isLiked}
                      isDisLiked={isDisLiked}
                    />
                  ) : (
                    <p>{messageObj.message}</p>
                  )}
                </span>

                {/* Render charts from the chartPhotos array */}
                {messageObj.chartPhotos.length > 0 && (
                  <div className="charts-container">
                    {messageObj.chartPhotos.map((chart, idx) => (
                      <div
                        key={idx}
                        className="image-wrapper w-100 p-3"
                        style={{ position: "relative" }}
                      >
                        <img
                          id={`chart-${index}-${idx}`}
                          className="chart-image w-100"
                          src={`data:image/png;base64,${chart}`}
                          alt={`Chart ${idx + 1}`}
                          style={{
                            cursor: "pointer",
                            objectFit: "contain",
                            maxHeight: "200px",
                          }}
                        />
                        {/* Image Options */}
                        <div className="image-overlay">
                          <Button
                            variant="light"
                            onClick={() =>
                              handleDownloadNarrativePDF(messageObj, index)
                            }
                            title="Download Image"
                            style={{ marginRight: "10px", color: "black" }}
                          >
                            <DownloadOutlined title="Download Image" />
                          </Button>
                          <Button
                            variant="primary"
                            onClick={() =>
                              handleImageClick(`data:image/png;base64,${chart}`)
                            }
                            title="Preview Image"
                          >
                            <EyeFilled title="Preview Image" size={30} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div id="fetching-message" className="fetching-message blinking">
                {statusMessage} {/* Dynamically display the status message */}
              </div>
            )}

            <div ref={chatEndRef} />

            {/* Modal for Image Preview */}

            <Modal
              size="lg"
              centered
              open={showModal && previewImage}
              footer={null}
              onCancel={() => setShowModal(false)}
            >
              <div>
                {previewImage && (
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{ width: "100%" }}
                  />
                )}
              </div>
            </Modal>
          </div>
          {showAttachmentOptions && (
            <AttachmentOptions
              isVisible={showAttachmentOptions}
              setShowAttachmentOptions={setShowAttachmentOptions}
              user={userDetails._id}
              setLoading={setLoading}
              setStatusMessage={setStatusMessage}
            />
          )}
          <div className="input-container">
            {/* <button
              className=" submit-button"
              title="Attach File"
              onClick={toggleAttachmentOptions}
              style={{ right: "unset", left: "1px" }}
            >
              <PaperClipOutlined /> 
            </button>  */}

            <input
              type="text"
              id="input-box"
              className="input-box"
              placeholder="Ask your question here..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)} // Update state on input change
              onKeyDown={(e) => {
                if (e.key === "Enter" && userInput.trim() && !loading) {
                  handleChatSubmit(); 
                }
              }}
              autoComplete="off"
              onInput={(e) => {
                const value = e.target.value.trim();
                console.log("value", value);
                if (value.length === 5 || value.length === 10) {
                  handleSuggestions(e); // Call API only at 5th and 10th character
                } else if (value.length === 0) {
                  console.log("calling clear");
                  clearSuggestions();
                }
              }}
            />
            {suggestions.length > 0 && userInput.length > 0 && (
              <div id="suggestions-dropdown" className="suggestions-dropdown">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}

            <button
              className="submit-button"
              title="Send Message"
              onClick={() => {
                if (userInput.trim() && !loading) {
                  // Allow only if input is not empty
                  handleChatSubmit();
                }
              }}
              disabled={!userInput.trim()} 
            >
              <SendOutlined /> {/* Ant Design Send Icon */}
            </button>

            <MoreOutlined
              className="three-dots-button"
              onClick={toggleIconSlider}
              title="More Options"
            />
            {/* Vertical icons (microphone, cancel, reset) */}
            {showIcons && (
              <div className="icon-slider">
                <button
                  id="microphone-button"
                  className="microphone-button"
                  title={
                    isListening ? "Listening..." : "Start Voice Recognition"
                  }
                  onClick={startVoiceRecognition} // Start recognition here
                >
                  <AudioOutlined
                    style={{
                      animation: isListening ? "blink 1s infinite" : "none",
                    }}
                  />{" "}
                  {/* Ant Design Audio (Mic) Icon */}
                </button>
                <button
                  id="cancel-button"
                  className="microphone-button"
                  title="Cancel Request"
                  onClick={handleCancelRequest}
                >
                  <StopOutlined /> {/* Ant Design Cancel Icon */}
                </button>
                <button
                  id="reset-button"
                  className="microphone-button"
                  title="Reset Screen"
                  onClick={handleReset}
                >
                  <ReloadOutlined /> {/* Ant Design Reset Icon */}
                </button>
              </div>
            )}
          </div>
          {/* <div id="loading-spinner" className="loading-spinner"></div> */}
          {loading && (
            <div id="loading-spinner" className="loading-spinner"></div>
          )}
        </div>
      )}
    </>
  );
};

export default FooterComponent;
